import { Form } from "antd";
import { useEffect, useRef, useState } from "react";

import type FixMeLater from "../../../../shared/types/fixMeLater.type";
import useValue from "../../../../hooks/useValue";
import { ConfigMasterConstants, ModuleAndIdMapping, Modules } from "../../../../constants/DatabaseConstants/MasterConstant";
import { CustomNotification } from "../../../../util/CustomNotification";
import * as masterServices from "../../../../services/masterServices";
import { PaymentGatewayConstant } from "../../../../constants/DatabaseConstants/ABRConstants";

type CustomerMasterProps = {
  saving: boolean;
  addRecord: boolean;
  handleAddRecord: (value: boolean) => void;
  rowData: FixMeLater;
  handleClearRowIdAndActionType: () => void;
  handleSubmit: (data: FixMeLater) => void;
};

export const useCustomerMaster = (props: CustomerMasterProps) => {
  /* CONSTANTS */
  const [form] = Form.useForm();
  const formRef = useRef<FixMeLater>(null);
  const allowedPaymentGatewayOptionList = [
    { label: PaymentGatewayConstant.BILLDESK, value: PaymentGatewayConstant.BILLDESK },
    { label: PaymentGatewayConstant.RAZORPAY, value: PaymentGatewayConstant.RAZORPAY },
    { label: PaymentGatewayConstant.OFFLINE, value: PaymentGatewayConstant.OFFLINE }
  ];

  /* STATES */
  const [formFieldsLoadedToForm, setFormFieldsLoadedToForm] = useState(false);
  const [imageFile, setImageFile] = useState<FixMeLater>();
  const [imageUrl, setImageUrl] = useState<FixMeLater>();
  const [themeValue, setThemeValue] = useState<FixMeLater | null>(null);

  /* CUSTOM HOOKS */
  const { value: loading, handleEnableValue: handleEnableLoading, handleDisableValue: handleDisableLoading } = useValue(false);

  /* HANDLERS */
  const getConfigValue = (getConfigList: FixMeLater, key: string) => {
    return getConfigList?.find((item: { key: string }) => item?.key === key)?.value || "";
  };

  const resetThemeField = () => {
    const formValues = {
      header_text: null,
      footer_text: null,
      primary_color: null,
      secondary_color: null,
      logo_url: null
    };
    form.setFieldsValue(formValues);
    setImageFile(null);
    setImageUrl(null);
    setThemeValue(null);
  };

  const getConfigMasterList = async () => {
    try {
      const result = await masterServices.getOneMasterListById(ModuleAndIdMapping[Modules.CONFIG_MASTER]);
      return result?.["AllRecordsList"];
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    }
  };

  const handleSetDefaultTheme = async (checked: boolean) => {
    try {
      handleEnableLoading();
      form.setFieldsValue({ isDefaultTheme: checked });
      resetThemeField();

      const getConfigList = await getConfigMasterList();

      if (checked) {
        const formValues = {
          header_text: getConfigValue(getConfigList, ConfigMasterConstants.HEADER_TEXT),
          footer_text: getConfigValue(getConfigList, ConfigMasterConstants.FOOTER_TEXT),
          primary_color: getConfigValue(getConfigList, ConfigMasterConstants.PRIMARY_COLOR),
          secondary_color: getConfigValue(getConfigList, ConfigMasterConstants.SECONDARY_COLOR),
          logo_url: getConfigValue(getConfigList, ConfigMasterConstants.LOGO_IMAGE_PATH)
        };

        setThemeValue(formValues);
        form.setFieldsValue(formValues);
        setImageUrl(getConfigValue(getConfigList, ConfigMasterConstants.LOGO_IMAGE_PATH));
      }
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      setTimeout(() => {
        handleDisableLoading();
      }, 500);
    }
  };

  /* loading states from multiple hooks added as expression */
  const isOnAddLoading = !props.addRecord;
  const isOnEditLoading = !!props.addRecord;

  /* EFFECTS */
  useEffect(() => {
    if (!props.addRecord && !isOnEditLoading && !formFieldsLoadedToForm) {
      setImageUrl(props?.rowData?.logo_url);
      form.setFieldsValue(props.rowData);
      setFormFieldsLoadedToForm(true);
    }

    if (props.addRecord) handleSetDefaultTheme(true);
  }, [props.addRecord, isOnEditLoading, formFieldsLoadedToForm, props.rowData, form, setFormFieldsLoadedToForm]);

  return {
    form,
    formRef,
    imageFile,
    imageUrl,
    isOnAddLoading,
    isOnEditLoading,
    formFieldsLoadedToForm,
    setImageFile,
    handleSetDefaultTheme,
    loading,
    themeValue,
    allowedPaymentGatewayOptionList
  };
};
